import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, CheckCircle, AlertCircle, TrendingUp, Users, Award, Clock, Zap } from 'lucide-react';
import { clearanceSearchPricing } from '../../pricingData';
import { Button } from '../../components/ui/button';
import { PricingSection } from '../../components/PricingSection';
import { TestimonialSlider } from '../../components/TestimonialSlider';
import { ProcessSlider } from '../../components/ProcessSlider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { faqs } from '../../mock';
import { useScrollAnimation, useCounterAnimation } from '../../hooks/useScrollAnimation';

const StatCard = ({ icon: Icon, value, label, delay }) => {
  const [ref, count] = useCounterAnimation(parseInt(value), 2000);
  return (
    <div ref={ref} className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all" style={{ animationDelay: `${delay}ms` }}>
      <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon className="text-orange-600" size={32} />
      </div>
      <div className="text-4xl font-bold text-gray-900 mb-2">{count}+</div>
      <div className="text-gray-600">{label}</div>
    </div>
  );
};

export const ClearanceSearch = ({ onOpenPopup }) => {
  const [benefitsRef, benefitsVisible] = useScrollAnimation(0.2);
  const [statsRef, statsVisible] = useScrollAnimation(0.2);

  return (
    <div className="pt-20">
      <Helmet>
        <title>Clearance Search Services | Professional Legal Services</title>
        <meta name="description" content="Ensure brand uniqueness with comprehensive clearance searches" />
      </Helmet>

      <section className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-400 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-32 left-1/4 animate-float"><Shield className="w-20 h-20 text-white/10" /></div>
          <div className="absolute bottom-32 right-1/3 animate-float" style={{ animationDelay: '1s' }}><Award className="w-24 h-24 text-white/10" /></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6 animate-slide-in">
              <span className="text-sm font-semibold">⭐ Professional Service</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-slide-in">Clearance Search Services</h1>
            <p className="text-xl md:text-2xl text-orange-100 mb-10 leading-relaxed animate-slide-in" style={{ animationDelay: '0.1s' }}>
              Ensure brand uniqueness with comprehensive clearance searches
            </p>
            <div className="flex flex-wrap justify-center gap-4 animate-slide-in" style={{ animationDelay: '0.2s' }}>
              <Button size="lg" onClick={onOpenPopup} className="bg-white text-orange-600 hover:bg-orange-50 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all">
                Get Started Today
              </Button>
              <Button size="lg" variant="outline" onClick={onOpenPopup} className="border-2 border-white text-white hover:bg-white hover:text-orange-600 shadow-xl">
                Live Chat
              </Button>
            </div>
          </div>
        </div>
      </section>

      

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-6">
                <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all h-64">
                  <img 
                    src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&h=400&fit=crop" 
                    alt="Legal documents and trademark search" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-orange-900/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg">
                      <Shield className="w-8 h-8 text-white mb-1" />
                      <p className="text-white text-sm font-semibold">Professional Search</p>
                    </div>
                  </div>
                </div>
                <div className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all mt-8 h-64">
                  <img 
                    src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=600&h=400&fit=crop" 
                    alt="Business analytics and trademark clearance" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-orange-900/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-white/20 backdrop-blur-sm p-3 rounded-lg">
                      <CheckCircle className="w-8 h-8 text-white mb-1" />
                      <p className="text-white text-sm font-semibold">Detailed Analysis</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Our Search Process</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">1</div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Federal Database Search</h3>
                    <p className="text-gray-600">Comprehensive search of USPTO trademark database for existing registrations.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">2</div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">State & Common Law Search</h3>
                    <p className="text-gray-600">Investigation of state registrations and unregistered common law uses.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">3</div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Domain & Business Name Search</h3>
                    <p className="text-gray-600">Analysis of domain registrations and business entity names.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-bold">4</div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Detailed Report & Recommendations</h3>
                    <p className="text-gray-600">Comprehensive findings with expert legal recommendations.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PricingSection pricingData={clearanceSearchPricing} onOpenPopup={onOpenPopup} />
      <ProcessSlider />
      <TestimonialSlider />

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Everything you need to know about our clearance search services</p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.slice(0, 6).map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-orange-50 rounded-xl px-6 border-none">
                <AccordionTrigger className="text-left hover:no-underline py-6">
                  <span className="text-lg font-semibold text-gray-900">{faq.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-orange-600 via-orange-500 to-orange-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Protect Your Brand?</h2>
            <p className="text-xl text-orange-100 mb-10 leading-relaxed">
              Start your clearance search today and ensure your brand name is legally available.
            </p>
            <Button size="lg" onClick={onOpenPopup} className="bg-white text-orange-600 hover:bg-orange-50 shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all">
              Get Started Now →
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};